import React from 'react';
import { ISelectOption, TSelectOptionId } from 'components/Select/types';
import { Icon, IconName } from 'components/Icon/Icon';
import { NewButton } from 'components/Button/NewButton/NewButton';
import Text from 'components/Text/Text';
import classNames from 'classnames';

interface IProps {
	options: ISelectOption[];
	value: TSelectOptionId[];
	withSelectAll: boolean;
	onChange: (value: TSelectOptionId[]) => void;
}

export const SelectAllHeader: React.FC<IProps> = ({ options, value, withSelectAll, onChange }) => {
	const enabledIds = new Set<TSelectOptionId>(options.filter((el) => !el.disabled).map((el) => el.id));
	const disabledIds = new Set<TSelectOptionId>(options.filter((el) => el.disabled).map((el) => el.id));

	const isAllSelected = enabledIds.size > 0 && [...enabledIds].every((id) => value.includes(id));
	const isSelectAllDisabled = !enabledIds.size;
	const valueAfterClear = value.filter((el) => disabledIds.has(el));
	const canClear = valueAfterClear.length < value.length;

	const handleSelectAll = () => {
		if (isSelectAllDisabled) {
			return;
		}
		onChange(
			isAllSelected
				? value.filter((el) => !enabledIds.has(el))
				: [...value, ...[...enabledIds].filter((id) => !value.includes(id))],
		);
	};

	const handleClearAll = () => {
		onChange(valueAfterClear);
	};

	const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
		e.preventDefault();
	};

	return (
		<div onMouseDown={handleMouseDown}>
			<div className="h-10 px-4 flex items-center justify-between gap-2">
				<Text
					className="!text-grey-06-new"
					translationNameSpace="form"
					translationKey="selectAnyItemPlaceholder"
					size="xs"
				/>
				<NewButton
					className="!min-w-0 !p-0"
					variant="ghost"
					size="small"
					disabled={!canClear}
					onClick={handleClearAll}>
					<Text translationNameSpace="form" translationKey="clearAllPlaceholder" asFragment={true} />
				</NewButton>
			</div>
			{withSelectAll && (
				<>
					<div
						className={classNames('h-10 px-4 flex items-center gap-2', {
							'cursor-pointer': !isSelectAllDisabled,
							'cursor-not-allowed': isSelectAllDisabled,
						})}
						role="checkbox"
						aria-checked={isAllSelected}
						aria-disabled={isSelectAllDisabled}
						onClick={handleSelectAll}>
						<Icon
							name={isAllSelected ? IconName.checkboxChecked : IconName.checkboxUnchecked}
							size="lg"
							className={isSelectAllDisabled ? 'text-grey-04-new' : 'text-purple-07-new'}
						/>
						<Text
							className={classNames({ '!text-grey-04-new': isSelectAllDisabled })}
							translationNameSpace="form"
							translationKey="selectAllPlaceholder"
							size="xs"
						/>
					</div>
					<div className="h-4 px-4 flex items-center">
						<div className="w-full h-px bg-grey-05-new" />
					</div>
				</>
			)}
		</div>
	);
};
